import AsyncStorage from '@react-native-async-storage/async-storage';
import { ParamListBase } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import { useCallback, useState } from 'react';
import { StyleSheet } from 'react-native';

import { useAppContext } from '../components/AppContext';
import { Reauthenticate } from '../components/Reauthenticate';
import { View } from '../components/View';
import { useAppState } from '../hooks/useAppState';
import { useIsLoggedIn } from '../hooks/useIsLoggedIn';
import { pauseInteractionForMillis } from '../lib/pauseInteraction';

export function createSessionTimeoutStackNavigator<T extends ParamListBase>(timeout: number) {
  const { Navigator, Screen, Group } = createStackNavigator<T>();
  return {
    Group,
    Navigator: ((props) => {
      const { navigationContainer } = useAppContext();
      const isLoggedIn = useIsLoggedIn();
      const [showReauthentication, setShowReauthentication] = useState(false);

      useAppState(
        useCallback(
          (nextAppState) => {
            if (nextAppState === 'active') {
              void AsyncStorage.getItem('lastSeen').then((lastSeenStr) => {
                const lastSeen = Number.parseInt(lastSeenStr || '');
                if (!Number.isFinite(lastSeen) || Date.now() - lastSeen > timeout) {
                  if (isLoggedIn) {
                    pauseInteractionForMillis(500);
                    setShowReauthentication(true);
                  }
                }
              });
            } else if (!showReauthentication) {
              void AsyncStorage.setItem('lastSeen', Date.now().toString());
            }
          },
          [isLoggedIn, showReauthentication],
        ),
      );

      const inner = <Navigator {...props}>{props.children}</Navigator>;

      return (
        <>
          <View aria-hidden={showReauthentication} style={{ flex: 1 }}>
            {inner}
          </View>
          {showReauthentication ? (
            <View style={[StyleSheet.absoluteFillObject]}>
              <Reauthenticate
                onSuccess={() => setShowReauthentication(false)}
                onLogout={(forgotPassword) => {
                  setTimeout(() => {
                    setShowReauthentication(false);
                    if (forgotPassword) {
                      // @ts-ignore
                      navigationContainer?.navigate('RequestResetPassword', {});
                    }
                  }, 100);
                }}
              />
            </View>
          ) : null}
        </>
      );
    }) as typeof Navigator,
    Screen,
  };
}
